import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  ArrowRight,
  Bell,
  Bot,
  Check,
  CheckCircle,
  Database,
  MessageCircle,
  Star,
  Users,
  Zap,
} from "lucide-react";
import React from "react";
import Footer from "./Footer";
import Header from "./Header";
import MiniFooter from "./MiniFooter";

const testimonials = [
  {
    name: "Marie Dubois",
    title: "Avocate d'affaires",
    content:
      "Docuveritas a révolutionné ma pratique. L'accès instantané aux documents et la certification par experts me font gagner un temps précieux.",
    avatar: "/placeholder.svg?height=60&width=60",
  },
  {
    name: "Pierre Martin",
    title: "Juriste d'entreprise",
    content:
      "L'IA comprend parfaitement mes questions complexes et les réponses certifiées me donnent une confiance totale dans mes décisions.",
    avatar: "/placeholder.svg?height=60&width=60",
  },
  {
    name: "Sophie Laurent",
    title: "Directrice juridique",
    content:
      "Les alertes personnalisées me permettent de rester à jour sur tous les développements juridiques pertinents pour mon secteur.",
    avatar: "/placeholder.svg?height=60&width=60",
  },
];

const partners = [
  "Barreau de Paris",
  "Association des Juristes",
  "Chambre de Commerce",
  "Institut Juridique",
];

const plans = [
  {
    name: "Free Trial",
    price: "Gratuit",
    period: "14 jours",
    description: "Découvrez Docuveritas sans engagement",
    features: [
      "50 recherches par mois",
      "10 questions IA par jour",
      "Accès base documentaire limitée",
      "Support par email",
    ],
    cta: "Commencer l'essai",
    popular: false,
  },
  {
    name: "Standard",
    price: "99€",
    period: "par mois",
    description: "Pour les professionnels individuels",
    features: [
      "Recherches illimitées",
      "100 questions IA par jour",
      "Accès complet base documentaire",
      "5 certifications par mois",
      "Alertes personnalisées",
      "Support prioritaire",
    ],
    cta: "Choisir ce plan",
    popular: true,
  },
  {
    name: "Premium",
    price: "299€",
    period: "par mois",
    description: "Pour les équipes et cabinets",
    features: [
      "Tout du plan Standard",
      "Questions IA illimitées",
      "Certifications illimitées",
      "Gestion d'équipe",
      "API access",
      "Support dédié 24/7",
      "Formation personnalisée",
    ],
    cta: "Choisir ce plan",
    popular: false,
  },
];

export default function HomePage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4 text-center">
          <Badge className="mb-4 bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
            🚀 Plateforme en développement - Accès anticipé disponible
          </Badge>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
            Insights Juridiques Propulsés par l'IA,{" "}
            <span className="text-blue-600">Certifiés par des Experts</span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
            Accédez à une vaste base de documents juridiques, obtenez des
            réponses instantanées grâce à l'IA, et faites-les valider par des
            experts certifiés.
          </p>
          <Button
            size="lg"
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 text-lg"
          >
            Inscrivez-vous maintenant
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>

        <div className="relative max-w-5xl mx-auto mt-12">
          <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-2">
                <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                <div className="w-3 h-3 bg-green-400 rounded-full"></div>
              </div>
            </div>
            <div>
              <img
                src="/Capture chatbot.png"
                alt="Dashboard Docuveritas"
                className="w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Fonctionnalités Principales
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez comment Docuveritas révolutionne l'accès à l'information
              juridique
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Database className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl text-gray-900">
                  Base de Données Juridique Complète
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 text-center">
                  Accédez à des milliers de documents juridiques organisés avec
                  une recherche avancée et des filtres.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bot className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl text-gray-900">
                  Chatbot IA Intelligent
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 text-center">
                  Posez des questions juridiques et recevez des réponses
                  immédiates générées par l'IA.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl text-gray-900">
                  Certification par des Experts
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 text-center">
                  Demandez la validation des réponses de l'IA par des experts
                  juridiques enregistrés.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-gray-200 hover:shadow-lg transition-shadow">
              <CardHeader className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Bell className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-xl text-gray-900">
                  Alertes Personnalisées
                </CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-600 text-center">
                  Restez informé avec des notifications sur les nouveaux
                  documents pertinents.
                </CardDescription>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comment ça marche
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Un processus simple en 4 étapes pour obtenir des réponses
              juridiques fiables
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="h-10 w-10 text-white" />
              </div>
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Posez votre question
              </h3>
              <p className="text-gray-600">
                Posez une question juridique au chatbot IA en utilisant un
                langage naturel.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Zap className="h-10 w-10 text-white" />
              </div>
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Réponse instantanée
              </h3>
              <p className="text-gray-600">
                Obtenez une réponse instantanée de l'IA basée sur notre base de
                données juridique.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-white" />
              </div>
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Demandez une certification
              </h3>
              <p className="text-gray-600">
                Demandez éventuellement une certification par un expert
                juridique qualifié.
              </p>
            </div>

            <div className="text-center">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-white" />
              </div>
              <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-4 text-sm font-bold">
                4
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                Réponse vérifiée
              </h3>
              <p className="text-gray-600">
                Recevez une réponse vérifiée et certifiée par un expert
                juridique.
              </p>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Testimonials */}
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Découvrez ce que disent les professionnels du droit qui utilisent
              Docuveritas
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
            {testimonials.map((testimonial, index) => (
              <Card
                key={index}
                className="border-2 hover:border-slate-300 transition-colors rounded-xl"
              >
                <CardContent className="p-8">
                  <div className="flex mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="h-5 w-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <p className="text-gray-600 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center">
                    <img
                      src={testimonial.avatar || "/placeholder.svg"}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full mr-4"
                    />
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {testimonial.name}
                      </h4>
                      <p className="text-sm text-gray-600">
                        {testimonial.title}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Partners */}
          <div className="text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-8">
              Partenaires et institutions
            </h3>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
              {partners.map((partner, index) => (
                <div key={index} className="text-lg font-medium text-gray-400">
                  {partner}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="pricing" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Choisissez votre plan
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Des tarifs transparents adaptés à vos besoins professionnels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {plans.map((plan, index) => (
              <Card
                key={index}
                className={`relative rounded-2xl ${
                  plan.popular
                    ? "border-2 border-slate-500 shadow-xl scale-105"
                    : "border-2 border-gray-200 hover:border-slate-300"
                } transition-all duration-300`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-slate-500 text-white px-4 py-2 rounded-full text-sm font-medium">
                      Plus populaire
                    </span>
                  </div>
                )}

                <CardHeader className="text-center pb-8">
                  <CardTitle className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </CardTitle>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-slate-500">
                      {plan.price}
                    </span>
                    {plan.period && (
                      <span className="text-gray-600 ml-2">
                        / {plan.period}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-600">{plan.description}</p>
                </CardHeader>

                <CardContent className="pt-0">
                  <ul className="space-y-4 mb-8">
                    {plan.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-start">
                        <Check className="h-5 w-5 text-green-500 mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <Button
                    className={`w-full py-3 rounded-xl ${
                      plan.popular
                        ? "bg-slate-500 hover:bg-slate-600 text-white"
                        : "bg-white border-2 border-slate-500 text-slate-500 hover:bg-slate-50"
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <p className="text-gray-600 mb-4">
              Besoin d'une solution sur mesure pour votre organisation ?
            </p>
            <Button
              variant="outline"
              className="border-slate-500 text-slate-500 hover:bg-slate-50 bg-transparent"
            >
              Contactez notre équipe
            </Button>
          </div>
        </div>
      </section>
      <MiniFooter />

      <Footer />
    </div>
  );
}
